import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useChurch } from "@/hooks/useChurch";
import { DollarSign, Download, Plus, Pencil, Trash2, MoreHorizontal, TableProperties, Upload, ArrowUpDown, ArrowUp, ArrowDown, TrendingDown, TrendingUp, Wallet, ArrowLeftRight, CalendarRange, FolderKanban, FileText, Landmark, Scale, CalendarIcon, Check, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import MemberNameCell from "@/components/MemberNameCell";
import { Input } from "@/components/ui/input";
import TableFilterBar from "@/components/table/TableFilterBar";
import TableSettingsGear, { TableColumnsPanel } from "@/components/table/TableSettingsGear";
import type { FilterGroup } from "@/components/table/TableFilterBar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  PieChart, Pie, Cell, Sector, ResponsiveContainer,
} from "recharts";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn, sortGeneralFirst, sortGeneralFirstByName } from "@/lib/utils";
import StatCard from "@/components/StatCard";
import BulkEntryTable from "@/components/finance/BulkEntryTable";
import CsvImport from "@/components/finance/CsvImport";
import AnnualBudget from "@/components/finance/AnnualBudget";
import ProjectBudgets from "@/components/finance/ProjectBudgets";
import * as ReportsHubModule from "@/components/finance/ReportsHub";
import BankingTab from "@/components/finance/BankingTab";
import MemberAutocomplete from "@/components/finance/MemberAutocomplete";
import FinanceFormDialog from "@/components/FinanceFormDialog";
import { startOfWeek, endOfWeek, format, parseISO, startOfYear, endOfYear } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useFreemium } from "@/hooks/useFreemium";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DEFAULT_INCOME_CATEGORIES = ["Tithes", "Offerings", "Donations", "Other"];
const DEFAULT_EXPENSE_CATEGORIES = ["Building Fee", "Insurance", "Salaries", "Missions", "Supplies", "Foods/drinks"];
const sortIncomeCategoriesWithPriority = (categories: string[]) => {
  const priority: Record<string, number> = {
    tithes: 0,
    offerings: 1,
  };
  return [...categories].sort((a, b) => {
    const aRank = priority[a.trim().toLowerCase()] ?? Number.MAX_SAFE_INTEGER;
    const bRank = priority[b.trim().toLowerCase()] ?? Number.MAX_SAFE_INTEGER;
    if (aRank !== bRank) return aRank - bRank;
    return a.localeCompare(b, undefined, { sensitivity: "base" });
  });
};
const MINISTRY_INCOME_BAR_COLOR = "#1e3a8a";
const MINISTRY_EXPENSE_BAR_COLOR = "#f59e0b";
const MINISTRY_INCOME_RING_COLORS = ["#1e3a8a", "#2563eb", "#3b82f6", "#60a5fa", "#93c5fd", "#1d4ed8"];
const MINISTRY_EXPENSE_RING_COLORS = ["#f59e0b", "#f97316", "#fb923c", "#fbbf24", "#fdba74", "#ea580c"];
const MINISTRY_NOTE_PREFIX = "Ministry: ";
const MINISTRY_IDS_NOTE_PREFIX = "Ministry IDs: ";
const LEGACY_ORG_NOTE_PREFIX = "Organization: ";
const LEGACY_ORG_IDS_NOTE_PREFIX = "Organization IDs: ";
const NEW_INLINE_EDIT_ID = "__new_inline_record__";
const INLINE_INSERT_TOP = "__insert_top__";

type SheetRecord = FinanceRecord | null;
const ReportsHub = (ReportsHubModule as any).default;

interface FinanceRecord {
  id: string;
  member_id: string | null;
  member_name: string | null;
  amount: number;
  date: string;
  category: string;
  record_type: string;
  payment_method: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

const getOrganizationFromNotes = (notes: string | null) => {
  if (!notes) return "";
  const firstLine = notes.split("\n")[0]?.trim() || "";
  if (firstLine.startsWith(MINISTRY_NOTE_PREFIX)) return firstLine.replace(MINISTRY_NOTE_PREFIX, "").trim();
  if (firstLine.startsWith(LEGACY_ORG_NOTE_PREFIX)) return firstLine.replace(LEGACY_ORG_NOTE_PREFIX, "").trim();
  return "";
};

const getOrganizationIdsFromNotes = (notes: string | null) => {
  if (!notes) return [] as string[];
  const lines = notes.split("\n").map((line) => line.trim());
  const idsLine = lines.find((line) =>
    line.startsWith(MINISTRY_IDS_NOTE_PREFIX) || line.startsWith(LEGACY_ORG_IDS_NOTE_PREFIX)
  );
  if (!idsLine) return [] as string[];
  return idsLine
    .replace(MINISTRY_IDS_NOTE_PREFIX, "")
    .replace(LEGACY_ORG_IDS_NOTE_PREFIX, "")
    .split(">")
    .map((id) => id.trim())
    .filter(Boolean);
};

type OrganizationNode = {
  id: string;
  name: string;
  parent_id: string | null;
};

const Finance = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile } = useChurch();
  const { role, user } = useAuth();
  const { can, getFinanceOrganizationIds, hasFinanceScopedAccess } = usePermissions();
  const { isPreview, requireSubscription } = useFreemium();
  const [inlineEditId, setInlineEditId] = useState<string | null>(null);
  const [inlineEditValues, setInlineEditValues] = useState<{
    record_type: "income" | "expense";
    date: string;
    category: string;
    organization: string;
    member_id: string | null;
    member_name: string;
    payment_method: string;
    amount: string;
  } | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [viewYear, setViewYear] = useState(String(new Date().getFullYear()));
  const [entryMode, setEntryMode] = useState<"none" | "bulk" | "import">("none");
  const [recordSearch, setRecordSearch] = useState("");
  const [typeFilters, setTypeFilters] = useState<Set<string>>(new Set());
  const [categoryFilters, setCategoryFilters] = useState<Set<string>>(new Set());
  const [ministryFilters, setMinistryFilters] = useState<Set<string>>(new Set());
  const [paymentFilters, setPaymentFilters] = useState<Set<string>>(new Set());
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [dateRangeStart, setDateRangeStart] = useState<Date | undefined>(startOfYear(new Date()));
  const [dateRangeEnd, setDateRangeEnd] = useState<Date | undefined>(new Date());

  // If this screen is opened with a stale range from a prior year, reset to current-year YTD.
  useEffect(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const startYear = dateRangeStart?.getFullYear();
    const endYear = dateRangeEnd?.getFullYear();
    if ((startYear && startYear < currentYear) || (endYear && endYear < currentYear)) {
      setDateRangeStart(startOfYear(now));
      setDateRangeEnd(now);
    }
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [activeDonut, setActiveDonut] = useState<{ ring: "income" | "expense"; index: number } | null>(null);
  const [inlineInsertAfterId, setInlineInsertAfterId] = useState<string | null>(null);
  const [inlineErrors, setInlineErrors] = useState<Set<string>>(new Set());
  const [selectedRecord, setSelectedRecord] = useState<FinanceRecord | null>(null);
  const toggleTypeFilter = (val: string) => {
    setTypeFilters((prev) => { const next = new Set(prev); if (next.has(val)) next.delete(val); else next.add(val); setCurrentPage(1); return next; });
  };
  const toggleCategoryFilter = (val: string) => {
    setCategoryFilters((prev) => { const next = new Set(prev); if (next.has(val)) next.delete(val); else next.add(val); setCurrentPage(1); return next; });
  };
  const toggleMinistryFilter = (val: string) => {
    setMinistryFilters((prev) => { const next = new Set(prev); if (next.has(val)) next.delete(val); else next.add(val); setCurrentPage(1); return next; });
  };
  const togglePaymentFilter = (val: string) => {
    setPaymentFilters((prev) => { const next = new Set(prev); if (next.has(val)) next.delete(val); else next.add(val); setCurrentPage(1); return next; });
  };
  const [finSortCol, setFinSortCol] = useState<string>("date");
  const [finSortDir, setFinSortDir] = useState<"asc" | "desc" | null>("desc");
  type FinColKey = "date" | "type" | "category" | "organization" | "member" | "payment" | "amount";
  const FIN_COL_DEFS: { key: FinColKey; label: string; sortField: string }[] = [
    { key: "type", label: "Type", sortField: "record_type" },
    { key: "date", label: "Date", sortField: "date" },
    { key: "category", label: "Category", sortField: "category" },
    { key: "organization", label: "Ministry", sortField: "organization" },
    { key: "member", label: "Source", sortField: "member_name" },
    { key: "payment", label: "Payment", sortField: "payment_method" },
    { key: "amount", label: "Amount", sortField: "amount" },
  ];
  const [finColOrder, setFinColOrder] = useState<FinColKey[]>(FIN_COL_DEFS.map((c) => c.key));
  const finColumns = finColOrder.map((k) => FIN_COL_DEFS.find((c) => c.key === k)!);
  const FIN_COL_WIDTH_CLASS: Record<FinColKey, string> = {
    type: "w-[10%]",
    date: "w-[13%]",
    category: "w-[13%]",
    organization: "w-[18%]",
    member: "w-[15%]",
    payment: "w-[13%]",
    amount: "w-[10%]",
  };
  const [visibleFinCols, setVisibleFinCols] = useState<Set<FinColKey>>(new Set(["date", "type", "category", "organization", "member", "payment", "amount"]));
  const [columnsOpen, setColumnsOpen] = useState(false);
  const toggleFinCol = (key: FinColKey) => {
    setVisibleFinCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) { if (next.size > 1) next.delete(key); }
      else next.add(key);
      return next;
    });
  };
  const toggleFinSort = (field: string) => {
    if (finSortCol === field) {
      if (finSortDir === "asc") setFinSortDir("desc");
      else if (finSortDir === "desc") { setFinSortCol(""); setFinSortDir(null); }
    } else { setFinSortCol(field); setFinSortDir("asc"); }
  };
  const FinSortIcon = ({ field, active }: { field: string; active: boolean }) => {
    if (!active) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-0 transition-opacity group-hover:opacity-70" />;
    }
    return finSortDir === "asc"
      ? <ArrowUp className="w-3 h-3 ml-1 opacity-100" />
      : <ArrowDown className="w-3 h-3 ml-1 opacity-100" />;
  };
  const queryClient = useQueryClient();
  const allowedFinanceOrgIds = getFinanceOrganizationIds();
  const financeScoped = role !== "admin" && hasFinanceScopedAccess();
  const canViewProjectBudgets = can("can_manage_budgets") || can("can_view_budgets");
  const canViewStatements = can("can_generate_reports") || can("can_generate_giving_statements");

  const { data: records = [], isLoading } = useQuery({
    queryKey: ["finance_records"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("finance_records")
        .select("*")
        .order("date", { ascending: false });
      if (error) throw error;
      return data as FinanceRecord[];
    },
  });

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations", "finance_scope"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, parent_id");
      if (error) throw error;
      return sortGeneralFirstByName((data || []) as OrganizationNode[]);
    },
  });

  const { data: members = [] } = useQuery({
    queryKey: ["members", "finance_member_lookup"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("members")
        .select("id, full_name");
      if (error) throw error;
      return data || [];
    },
  });

  const { data: profileRows = [] } = useQuery({
    queryKey: ["finance_profile_names"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, display_name");
      if (error) throw error;
      return data || [];
    },
  });

  const memberNameById = useMemo(() => {
    const map = new Map<string, string>();
    members.forEach((member: any) => {
      if (member.id) map.set(member.id, member.full_name || "");
    });
    return map;
  }, [members]);

  const resolveMemberName = (record: FinanceRecord) => {
    if (record.member_id) {
      const linkedName = memberNameById.get(record.member_id);
      if (linkedName) return linkedName;
    }
    return record.member_name || "";
  };

  const profileNameByUserId = useMemo(() => {
    const map = new Map<string, string>();
    profileRows.forEach((row: any) => {
      if (row.user_id) map.set(row.user_id, row.display_name || "Unknown User");
    });
    return map;
  }, [profileRows]);

  const formatRecordTimestamp = (timestamp?: string | null) => {
    if (!timestamp) return "—";
    const parsed = parseISO(timestamp);
    if (Number.isNaN(parsed.getTime())) return "—";
    return format(parsed, "MMMM d, yyyy  h:mma");
  };

  const scopedOrgSet = useMemo(() => {
    if (!financeScoped) return null;

    const byParent = new Map<string, string[]>();
    organizations.forEach((org) => {
      if (!org.parent_id) return;
      const current = byParent.get(org.parent_id) || [];
      current.push(org.id);
      byParent.set(org.parent_id, current);
    });

    const visited = new Set<string>();
    const queue = [...allowedFinanceOrgIds];
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      const children = byParent.get(currentId) || [];
      children.forEach((childId) => {
        if (!visited.has(childId)) queue.push(childId);
      });
    }

    return visited;
  }, [financeScoped, organizations, allowedFinanceOrgIds]);

  const organizationNameToIds = useMemo(() => {
    const map = new Map<string, string[]>();
    organizations.forEach((org) => {
      const key = org.name.toLowerCase();
      const existing = map.get(key) || [];
      existing.push(org.id);
      map.set(key, existing);
    });
    return map;
  }, [organizations]);

  const filteredByFinanceScope = useMemo(() => {
    if (!financeScoped || !scopedOrgSet) return records;

    return records.filter((record) => {
      const orgIdsFromNotes = getOrganizationIdsFromNotes(record.notes);
      if (orgIdsFromNotes.length > 0) {
        return orgIdsFromNotes.some((orgId) => scopedOrgSet.has(orgId));
      }

      const orgPath = getOrganizationFromNotes(record.notes);
      if (!orgPath) return false;
      const orgNames = orgPath.split(">").map((part) => part.trim().toLowerCase()).filter(Boolean);
      if (orgNames.length === 0) return false;

      return orgNames.some((name) => {
        const matchedIds = organizationNameToIds.get(name) || [];
        return matchedIds.some((id) => scopedOrgSet.has(id));
      });
    });
  }, [records, financeScoped, scopedOrgSet, organizationNameToIds]);

  const editableMinistryOptions = useMemo(() => {
    const orgPool = financeScoped && scopedOrgSet
      ? organizations.filter((org) => scopedOrgSet.has(org.id))
      : organizations;
    const names = orgPool
      .map((org) => (org.name || "").trim())
      .filter(Boolean);
    return sortGeneralFirst(Array.from(new Set(names)));
  }, [financeScoped, organizations, scopedOrgSet]);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("finance_records").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["finance_records"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_finance"] });
      toast.success("Record deleted");
      setDeleteId(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<FinanceRecord> }) => {
      const { error } = await supabase.from("finance_records").update(updates).eq("id", id);
      if (error) throw error;
    },
  });

  const yearRecords = useMemo(
    () => filteredByFinanceScope.filter((r) => {
      const rDate = new Date(r.date + "T00:00:00");
      if (dateRangeStart && rDate < dateRangeStart) return false;
      if (dateRangeEnd && rDate > dateRangeEnd) return false;
      return true;
    }),
    [filteredByFinanceScope, dateRangeStart, dateRangeEnd]
  );

  const filteredYearRecords = useMemo(() => {
    let result = yearRecords.filter((r) => {
      const organization = getOrganizationFromNotes(r.notes);
      const matchesSearch = !recordSearch ||
        resolveMemberName(r).toLowerCase().includes(recordSearch.toLowerCase()) ||
        r.category.toLowerCase().includes(recordSearch.toLowerCase()) ||
        organization.toLowerCase().includes(recordSearch.toLowerCase()) ||
        r.date.includes(recordSearch);
      const normalizedType = ((r.record_type || "income") === "expense" ? "Expense" : "Income");
      const matchesType = typeFilters.size === 0 || typeFilters.has(normalizedType);
      const matchesCategory = categoryFilters.size === 0 || categoryFilters.has(r.category);
      const matchesMinistry = ministryFilters.size === 0 || ministryFilters.has(organization || "—");
      const matchesPayment = paymentFilters.size === 0 || paymentFilters.has(r.payment_method || "—");
      return matchesSearch && matchesType && matchesCategory && matchesMinistry && matchesPayment;
    });
    if (finSortCol && finSortDir) {
      result = [...result].sort((a, b) => {
        let aVal: any, bVal: any;
        if (finSortCol === "amount") { aVal = Number(a.amount); bVal = Number(b.amount); return finSortDir === "asc" ? aVal - bVal : bVal - aVal; }
        if (finSortCol === "organization") {
          aVal = getOrganizationFromNotes(a.notes);
          bVal = getOrganizationFromNotes(b.notes);
          const cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: "base" });
          return finSortDir === "asc" ? cmp : -cmp;
        }
        if (finSortCol === "member_name") {
          aVal = resolveMemberName(a);
          bVal = resolveMemberName(b);
          const cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: "base" });
          return finSortDir === "asc" ? cmp : -cmp;
        }
        aVal = (a as any)[finSortCol] ?? "";
        bVal = (b as any)[finSortCol] ?? "";
        const cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: "base" });
        return finSortDir === "asc" ? cmp : -cmp;
      });
    }
    return result;
  }, [yearRecords, recordSearch, typeFilters, categoryFilters, ministryFilters, paymentFilters, finSortCol, finSortDir, memberNameById]);

  const totalPages = Math.max(1, Math.ceil(filteredYearRecords.length / pageSize));
  const pagedRecords = filteredYearRecords.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const isFinanceSearchEmpty = pagedRecords.length === 0 && yearRecords.length !== 0;
  const financeDisplayRangeStart = filteredYearRecords.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const financeDisplayRangeEnd = currentPage * pageSize;

  const ALL_TYPES = ["Income", "Expense"];
  const uniqueTypes = useMemo(() => {
    const fromData = filteredByFinanceScope.map((r) => ((r.record_type || "income") === "expense" ? "Expense" : "Income"));
    return [...new Set([...ALL_TYPES, ...fromData])].sort();
  }, [filteredByFinanceScope]);
  const ALL_PAYMENTS = ["Cash", "Cheque", "Transfer", "Mobile Money", "Card", "—"];
  const incomeCategoryOptions = useMemo(() => {
    const fromData = filteredByFinanceScope
      .filter((r) => (r.record_type || "income") !== "expense")
      .map((r) => r.category)
      .filter(Boolean);
    return sortIncomeCategoriesWithPriority([...new Set([...DEFAULT_INCOME_CATEGORIES, ...fromData])]);
  }, [filteredByFinanceScope]);
  const expenseCategoryOptions = useMemo(() => {
    const fromData = filteredByFinanceScope
      .filter((r) => (r.record_type || "income") === "expense")
      .map((r) => r.category)
      .filter(Boolean);
    return [...new Set([...DEFAULT_EXPENSE_CATEGORIES, ...fromData])].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [filteredByFinanceScope]);
  const categoryFilterOptions = useMemo(() => {
    const hasIncome = typeFilters.has("Income");
    const hasExpense = typeFilters.has("Expense");
    if (typeFilters.size === 0 || (hasIncome && hasExpense)) {
      return [...new Set([...incomeCategoryOptions, ...expenseCategoryOptions])]
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    }
    if (hasIncome) return incomeCategoryOptions;
    if (hasExpense) return expenseCategoryOptions;
    return [...new Set([...incomeCategoryOptions, ...expenseCategoryOptions])]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [typeFilters, incomeCategoryOptions, expenseCategoryOptions]);

  useEffect(() => {
    setCategoryFilters((prev) => {
      const allowed = new Set(categoryFilterOptions);
      const next = new Set(Array.from(prev).filter((category) => allowed.has(category)));
      return next.size === prev.size ? prev : next;
    });
  }, [categoryFilterOptions]);

  const uniqueMinistries = useMemo(() => {
    const orgNames = new Set(organizations.map((o) => (o.name || "").trim()).filter(Boolean));
    const fromData = filteredByFinanceScope
      .map((r) => getOrganizationFromNotes(r.notes) || "—")
      .filter((name) => name === "—" || orgNames.has(name));
    return sortGeneralFirst([...new Set(fromData)]);
  }, [filteredByFinanceScope, organizations]);
  const uniquePayments = useMemo(() => {
    const fromData = filteredByFinanceScope.map((r) => r.payment_method || "—");
    return [...new Set([...ALL_PAYMENTS, ...fromData])].sort();
  }, [filteredByFinanceScope]);

  const monthlyData = useMemo(() => {
    const map: Record<string, Record<string, number>> = {};
    MONTHS.forEach((m) => { map[m] = { Tithes: 0, Offerings: 0, Donations: 0, "Special Events": 0 }; });
    yearRecords.forEach((r) => {
      const monthIdx = parseInt(r.date.split("-")[1], 10) - 1;
      const month = MONTHS[monthIdx];
      if (month && map[month]) {
        map[month][r.category] = (map[month][r.category] || 0) + Number(r.amount);
      }
    });
    return MONTHS.map((m) => ({ month: m, ...map[m] }));
  }, [yearRecords]);

  const categoryTotals = useMemo(() => {
    const currentYear = String(new Date().getFullYear());
    const ytdStartDate = `${viewYear}-01-01`;
    const ytdEndDate =
      viewYear === currentYear
        ? format(new Date(), "yyyy-MM-dd")
        : format(endOfYear(new Date(Number(viewYear), 0, 1)), "yyyy-MM-dd");

    const totals: Record<string, number> = {};
    filteredByFinanceScope
      .filter((r) => r.date >= ytdStartDate && r.date <= ytdEndDate)
      .forEach((r) => {
      totals[r.category] = (totals[r.category] || 0) + Number(r.amount);
      });

    return Object.entries(totals).map(([name, value]) => ({ name, value }));
  }, [filteredByFinanceScope, viewYear]);

  const categoryYtdRange = useMemo(() => {
    const currentYear = String(new Date().getFullYear());
    const start = startOfYear(new Date(Number(viewYear), 0, 1));
    const end = viewYear === currentYear ? new Date() : endOfYear(new Date(Number(viewYear), 0, 1));
    return `${format(start, "MMM d, yyyy")} – ${format(end, "MMM d, yyyy")}`;
  }, [viewYear]);

  const incomeRecords = filteredYearRecords.filter((r) => r.record_type !== "expense");
  const expenseRecords = filteredYearRecords.filter((r) => r.record_type === "expense");
  const totalIncome = incomeRecords.reduce((sum, r) => sum + Number(r.amount), 0);
  const totalExpenses = expenseRecords.reduce((sum, r) => sum + Number(r.amount), 0);
  const ministriesYtdSeries = useMemo(() => {
    const totals = new Map<string, { income: number; expense: number }>();

    filteredYearRecords.forEach((record) => {
      const ministry = getOrganizationFromNotes(record.notes) || "Unassigned";
      const current = totals.get(ministry) || { income: 0, expense: 0 };
      const amount = Number(record.amount || 0);
      if ((record.record_type || "income") === "expense") {
        current.expense += amount;
      } else {
        current.income += amount;
      }
      totals.set(ministry, current);
    });

    return Array.from(totals.entries())
      .map(([name, values]) => ({
        name,
        income: values.income,
        expense: values.expense,
        total: values.income + values.expense,
      }))
      .sort((a, b) => b.total - a.total);
  }, [filteredYearRecords]);
  const ministriesYtdChartData = useMemo(() => {
    if (ministriesYtdSeries.length <= 6) return ministriesYtdSeries;
    const top = ministriesYtdSeries.slice(0, 6);
    const other = ministriesYtdSeries.slice(6).reduce(
      (sum, item) => ({ income: sum.income + item.income, expense: sum.expense + item.expense }),
      { income: 0, expense: 0 }
    );
    return (other.income > 0 || other.expense > 0)
      ? [...top, { name: "Other", income: other.income, expense: other.expense, total: other.income + other.expense }]
      : top;
  }, [ministriesYtdSeries]);
  const totalIncomeRangeLabel = useMemo(() => {
    const currentYear = String(new Date().getFullYear());
    if (viewYear === currentYear) {
      return `Jan – ${format(new Date(), "MMM")} ${viewYear}`;
    }

    if (yearRecords.length > 0) {
      const latestMonthIndex = Math.max(
        ...yearRecords.map((record) => parseInt(record.date.split("-")[1], 10) - 1)
      );
      return `Jan – ${MONTHS[latestMonthIndex]} ${viewYear}`;
    }

    return `Jan – Dec ${viewYear}`;
  }, [viewYear, yearRecords]);
  const currentMonth = new Date().getMonth();
  const thisMonthIncomeRecords = yearRecords.filter((r) => r.record_type !== "expense" && parseInt(r.date.split("-")[1], 10) - 1 === currentMonth);
  const thisMonthExpenseRecords = yearRecords.filter((r) => r.record_type === "expense" && parseInt(r.date.split("-")[1], 10) - 1 === currentMonth);
  const thisMonthRecords = thisMonthIncomeRecords;
  const monthlyIncome = thisMonthIncomeRecords.reduce((sum, r) => sum + Number(r.amount), 0);
  const thisMonthNet = thisMonthIncomeRecords.reduce((sum, r) => sum + Number(r.amount), 0) - thisMonthExpenseRecords.reduce((sum, r) => sum + Number(r.amount), 0);
  const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 });
  const weekEnd = endOfWeek(new Date(), { weekStartsOn: 0 });
  const weeklyIncome = incomeRecords
    .filter((r) => {
      const date = parseISO(r.date);
      return date >= weekStart && date <= weekEnd;
    })
    .reduce((sum, r) => sum + Number(r.amount), 0);

  const years = useMemo(() => {
    const yrs = new Set(filteredByFinanceScope.map((r) => r.date.split("-")[0]));
    yrs.add(String(new Date().getFullYear()));
    return Array.from(yrs).sort().reverse();
  }, [filteredByFinanceScope]);

  const exportCSV = () => {
    const headers = ["Date", "Category", "Amount", "Source", "Payment Method", "Notes"];
    const rows = yearRecords.map((r) => [
      r.date, r.category, r.amount, resolveMemberName(r), r.payment_method || "", r.notes || "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `finance-report-${viewYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported!");
  };

  const formatCurrency = (val: number) => `$${val.toLocaleString(undefined, { minimumFractionDigits: 0 })}`;

  const normalizeAmountInput = (value: string) => {
    const sanitized = value.replace(/[^0-9.]/g, "");
    const firstDot = sanitized.indexOf(".");
    if (firstDot === -1) return sanitized;
    const intPart = sanitized.slice(0, firstDot);
    const fracPart = sanitized.slice(firstDot + 1).replace(/\./g, "").slice(0, 2);
    return `${intPart}.${fracPart}`;
  };

  const formatAmountInput = (value: string) => {
    if (!value) return "";
    const [intRaw, frac] = value.split(".");
    const intNum = Number(intRaw || "0");
    const intWithCommas = intNum.toLocaleString();
    return frac !== undefined ? `${intWithCommas}.${frac}` : intWithCommas;
  };

  const handleEntryDone = () => setEntryMode("none");

  const startInlineEdit = (record: FinanceRecord) => {
    setInlineEditId(record.id);
    setInlineEditValues({
      record_type: ((record.record_type || "income") === "expense" ? "expense" : "income"),
      date: record.date,
      category: record.category || "",
      organization: getOrganizationFromNotes(record.notes) || "",
      member_id: record.member_id || null,
      member_name: resolveMemberName(record),
      payment_method: record.payment_method || "",
      amount: String(record.amount ?? ""),
    });
  };

  const cancelInlineEdit = () => {
    setInlineEditId(null);
    setInlineEditValues(null);
    setInlineInsertAfterId(null);
    setInlineErrors(new Set());
  };

  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetRecord, setSheetRecord] = useState<SheetRecord>(null);

  const openAddRecordSheet = () => {
    setSheetRecord(null);
    setSheetOpen(true);
  };

  const openEditRecordSheet = (record: FinanceRecord) => {
    setSheetRecord(record);
    setSheetOpen(true);
  };

  const startInlineCreate = () => {
    setInlineInsertAfterId(INLINE_INSERT_TOP);
    setInlineEditId(NEW_INLINE_EDIT_ID);
    setInlineEditValues({
      record_type: "income",
      date: format(new Date(), "yyyy-MM-dd"),
      category: "",
      organization: "",
      member_id: null,
      member_name: "",
      payment_method: "",
      amount: "",
    });
  };

  const saveInlineEdit = async (record: FinanceRecord) => {
    if (!inlineEditValues) return;

    const amount = Number(inlineEditValues.amount);
    const errors = new Set<string>();
    if (!Number.isFinite(amount) || amount <= 0) errors.add("amount");
    if (!inlineEditValues.category.trim()) errors.add("category");
    if (!inlineEditValues.payment_method.trim()) errors.add("payment_method");

    if (errors.size > 0) {
      setInlineErrors(errors);
      toast.error("Please fill in all required fields.");
      return;
    }
    setInlineErrors(new Set());

    const organization = inlineEditValues.organization.trim();
    const remainingNoteLines = (record.notes || "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .filter((line) =>
        !line.startsWith(MINISTRY_NOTE_PREFIX) &&
        !line.startsWith(MINISTRY_IDS_NOTE_PREFIX) &&
        !line.startsWith(LEGACY_ORG_NOTE_PREFIX) &&
        !line.startsWith(LEGACY_ORG_IDS_NOTE_PREFIX)
      );

    const nextNoteLines: string[] = [];
    if (organization) {
      nextNoteLines.push(`${MINISTRY_NOTE_PREFIX}${organization}`);
      const matchedOrgIds = organizationNameToIds.get(organization.toLowerCase()) || [];
      if (matchedOrgIds.length > 0) {
        nextNoteLines.push(`${MINISTRY_IDS_NOTE_PREFIX}${matchedOrgIds.join(">")}`);
      }
    }
    nextNoteLines.push(...remainingNoteLines);

    const memberName = inlineEditValues.member_name.trim();
    const matchedMember = members.find((m: any) => (m.full_name || "").trim().toLowerCase() === memberName.toLowerCase());
    const selectedMemberId = inlineEditValues.member_id || matchedMember?.id || null;

    if (record.id === NEW_INLINE_EDIT_ID) {
      if (!profile?.id) {
        toast.error("Missing church profile. Please refresh and try again.");
        return;
      }

      await supabase.from("finance_records").insert({
        church_id: profile.id,
        created_by: user?.id || null,
        record_type: inlineEditValues.record_type,
        date: inlineEditValues.date,
        category: inlineEditValues.category.trim(),
        member_name: memberName || null,
        member_id: selectedMemberId,
        payment_method: inlineEditValues.payment_method.trim() || null,
        amount,
        notes: nextNoteLines.length > 0 ? nextNoteLines.join("\n") : null,
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["finance_records"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard_finance"] }),
      ]);
      toast.success("Record added");
      cancelInlineEdit();
      return;
    }

    await updateMutation.mutateAsync({
      id: record.id,
      updates: {
        record_type: inlineEditValues.record_type,
        date: inlineEditValues.date,
        category: inlineEditValues.category.trim(),
        member_name: memberName || null,
        member_id: selectedMemberId,
        payment_method: inlineEditValues.payment_method.trim() || null,
        amount,
        notes: nextNoteLines.length > 0 ? nextNoteLines.join("\n") : null,
      },
    });

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["finance_records"] }),
      queryClient.invalidateQueries({ queryKey: ["dashboard_finance"] }),
    ]);
    toast.success("Record updated");
    cancelInlineEdit();
  };

  const activeDonutValue = useMemo(() => {
    if (!activeDonut) return null;
    const item = ministriesYtdChartData[activeDonut.index];
    if (!item) return null;
    return {
      ring: activeDonut.ring,
      name: item.name,
      value: Number(activeDonut.ring === "income" ? item.income : item.expense),
    };
  }, [activeDonut, ministriesYtdChartData]);

  const renderExpandedOuter = (props: any) => (
    <Sector
      {...props}
      outerRadius={(props.outerRadius || 0) + 5}
      cornerRadius={7}
      className="transition-all duration-200 ease-out"
    />
  );

  const renderExpandedInner = (props: any) => (
    <Sector
      {...props}
      innerRadius={Math.max((props.innerRadius || 0) - 1, 0)}
      outerRadius={(props.outerRadius || 0) + 1}
      cornerRadius={6}
      className="transition-all duration-200 ease-out"
    />
  );

  const activeTab = searchParams.get("tab") || "records";
  const handleTabChange = (nextTab: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("tab", nextTab);
      return params;
    });
  };

  return (
    <div className="space-y-6">
        {/* Tabs nav */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <div className="overflow-x-auto overflow-y-hidden overscroll-x-contain overscroll-y-none touch-pan-x sm:overflow-visible sm:overflow-y-visible sm:touch-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <TabsList className="h-9 min-w-max rounded-none bg-muted p-1 gap-1 flex-nowrap items-center">
            <TabsTrigger value="records" className="h-7 shrink-0 px-2.5 text-[11px] font-medium rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:px-3 sm:text-xs whitespace-nowrap">
              <ArrowLeftRight className="w-3.5 h-3.5 mr-1.5" />Transactions
            </TabsTrigger>
            <TabsTrigger value="annual_budget" className="h-7 shrink-0 px-2.5 text-[11px] font-medium rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:px-3 sm:text-xs whitespace-nowrap">
              <CalendarRange className="w-3.5 h-3.5 mr-1.5" />Annual Budget
            </TabsTrigger>
            {canViewProjectBudgets && (
              <TabsTrigger value="project_budgets" className="h-7 shrink-0 px-2.5 text-[11px] font-medium rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:px-3 sm:text-xs whitespace-nowrap">
                <FolderKanban className="w-3.5 h-3.5 mr-1.5" />Projects
              </TabsTrigger>
            )}
            {canViewStatements && (
              <TabsTrigger value="statements" className="h-7 shrink-0 px-2.5 text-[11px] font-medium rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:px-3 sm:text-xs whitespace-nowrap">
                <FileText className="w-3.5 h-3.5 mr-1.5" />Statements
              </TabsTrigger>
            )}
            <TabsTrigger value="banking" className="h-7 shrink-0 px-2.5 text-[11px] font-medium rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:px-3 sm:text-xs whitespace-nowrap">
              <Landmark className="w-3.5 h-3.5 mr-1.5" />Banking
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="records">
          {/* Summary Finance Cards */}
          <div className="rounded-3xl border border-[#d7e4f5] bg-gradient-to-br from-white to-[#f4f8ff] p-4 sm:p-5 mb-4 shadow-[0_8px_30px_-8px_rgba(2,6,23,0.10)]">
            <div className="grid grid-cols-1 lg:grid-cols-[1.35fr_2.65fr] items-start gap-3 sm:gap-4">
              <div className="px-5 py-4">
                  <div className="flex items-center justify-start">
                    {ministriesYtdChartData.length === 0 ? (
                      <div className="h-20 w-[210px] flex items-center justify-center text-sm text-muted-foreground">No YTD data.</div>
                    ) : (
                      <div className="grid grid-cols-[132px_minmax(0,1fr)] items-start gap-x-4 gap-y-2 w-full">
                        <div className="h-32 w-32 shrink-0 row-span-2">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={ministriesYtdChartData}
                                dataKey="income"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                innerRadius={40}
                                outerRadius={54}
                                paddingAngle={1.5}
                                cornerRadius={7}
                                stroke="none"
                                isAnimationActive
                                animationDuration={320}
                                animationEasing="ease-in-out"
                                activeIndex={activeDonut?.ring === "income" ? activeDonut.index : -1}
                                activeShape={renderExpandedOuter}
                                onMouseEnter={(_, idx) => setActiveDonut({ ring: "income", index: idx })}
                                onMouseLeave={() => setActiveDonut(null)}
                              >
                                {ministriesYtdChartData.map((entry, idx) => (
                                  <Cell key={`income-${entry.name}-${idx}`} fill={MINISTRY_INCOME_RING_COLORS[idx % MINISTRY_INCOME_RING_COLORS.length]} />
                                ))}
                              </Pie>
                              <Pie
                                data={ministriesYtdChartData}
                                dataKey="expense"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                innerRadius={24}
                                outerRadius={37}
                                paddingAngle={1.5}
                                cornerRadius={6}
                                stroke="none"
                                isAnimationActive
                                animationDuration={320}
                                animationEasing="ease-in-out"
                                activeIndex={activeDonut?.ring === "expense" ? activeDonut.index : -1}
                                activeShape={renderExpandedInner}
                                onMouseEnter={(_, idx) => setActiveDonut({ ring: "expense", index: idx })}
                                onMouseLeave={() => setActiveDonut(null)}
                              >
                                {ministriesYtdChartData.map((entry, idx) => (
                                  <Cell key={`expense-${entry.name}-${idx}`} fill={MINISTRY_EXPENSE_RING_COLORS[idx % MINISTRY_EXPENSE_RING_COLORS.length]} />
                                ))}
                              </Pie>
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="min-w-0 text-left col-start-2 self-end">
                          <div className="mb-0.5 min-h-[38px]">
                            {activeDonutValue ? (
                              <>
                                <p className={`text-lg font-bold leading-tight transition-colors duration-200 ${activeDonutValue.ring === "income" ? "text-[#1e3a8a]" : "text-[#f59e0b]"}`}>
                                  {formatCurrency(activeDonutValue.value)}
                                </p>
                                <p className={`text-xs font-semibold transition-colors duration-200 ${activeDonutValue.ring === "income" ? "text-[#1e3a8a]" : "text-[#f59e0b]"}`}>
                                  {activeDonutValue.name} {activeDonutValue.ring === "income" ? "Income" : "Expense"}
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="text-lg font-bold leading-tight opacity-0 transition-opacity duration-200">$0</p>
                                <p className="text-xs font-semibold opacity-0 transition-opacity duration-200">Placeholder</p>
                              </>
                            )}
                          </div>
                          <p className="text-xs font-medium text-muted-foreground">Ministry Financials</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {dateRangeStart && dateRangeEnd
                              ? `${format(dateRangeStart, "MMM d, yyyy")} - ${format(dateRangeEnd, "MMM d, yyyy")}`
                              : dateRangeStart
                                ? `From ${format(dateRangeStart, "MMM d, yyyy")}`
                                : dateRangeEnd
                                  ? `Up to ${format(dateRangeEnd, "MMM d, yyyy")}`
                                  : "All time"}
                          </p>
                        </div>
                        <div className="col-start-2 flex items-center justify-start gap-4">
                          <div className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <span className="w-2.5 h-2.5 rounded-full bg-[#1e3a8a]" /> Income
                          </div>
                          <div className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <span className="w-2.5 h-2.5 rounded-full bg-[#f59e0b]" /> Expense
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 items-start gap-3 sm:gap-4 lg:self-start">
                {/* Total Income */}
                <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
                  <CardContent className="px-5 py-5 min-h-[156px] flex flex-col justify-center">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-muted-foreground">Total Income</p>
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                        <TrendingUp className="w-4.5 h-4.5 text-primary" />
                      </div>
                    </div>
                    <p className="text-2xl font-bold font-heading text-foreground tracking-tight">{formatCurrency(totalIncome)}</p>
                    <p className="text-xs text-muted-foreground mt-1">{dateRangeStart && dateRangeEnd ? `${format(dateRangeStart, "MMM d, yyyy")} – ${format(dateRangeEnd, "MMM d, yyyy")}` : "All time"}</p>
                  </CardContent>
                </Card>
                {/* Total Expenses */}
                <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
                  <CardContent className="px-5 py-5 min-h-[156px] flex flex-col justify-center">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-muted-foreground">Total Expenses</p>
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                        <TrendingDown className="w-4.5 h-4.5 text-primary" />
                      </div>
                    </div>
                    <p className="text-2xl font-bold font-heading text-foreground tracking-tight">{formatCurrency(totalExpenses)}</p>
                    <p className="text-xs text-muted-foreground mt-1">{dateRangeStart && dateRangeEnd ? `${format(dateRangeStart, "MMM d, yyyy")} – ${format(dateRangeEnd, "MMM d, yyyy")}` : "All time"}</p>
                  </CardContent>
                </Card>
                {/* Net Balance */}
                <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
                  <CardContent className="px-5 py-5 min-h-[156px] flex flex-col justify-center">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-muted-foreground">Net Balance</p>
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${totalIncome - totalExpenses >= 0 ? "bg-success/10" : "bg-destructive/10"}`}>
                        <Scale className={`w-4.5 h-4.5 ${totalIncome - totalExpenses >= 0 ? "text-success" : "text-destructive"}`} />
                      </div>
                    </div>
                    <p className={`text-2xl font-bold font-heading tracking-tight ${totalIncome - totalExpenses >= 0 ? "text-success" : "text-destructive"}`}>
                      {totalIncome - totalExpenses < 0 ? "-" : ""}{formatCurrency(Math.abs(totalIncome - totalExpenses))}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Income minus expenses</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
          <div className="stat-card relative p-0 overflow-visible border-transparent">
          <div className="px-0 py-3 border-b border-transparent relative sm:px-4">
          <TableFilterBar
            search={recordSearch}
            onSearchChange={setRecordSearch}
            searchPlaceholder="Search records..."
            searchFirstOnMobile
            mobileControlsFirst
            compactDesktopFilters
            filterColumns={2}
            filterGroups={[
              { key: "ministry", label: "Ministry", options: uniqueMinistries, selected: ministryFilters, onToggle: toggleMinistryFilter, onClear: () => setMinistryFilters(new Set()) },
              { key: "type", label: "Type", options: uniqueTypes, selected: typeFilters, onToggle: toggleTypeFilter, onClear: () => setTypeFilters(new Set()) },
              { key: "category", label: "Category", options: categoryFilterOptions, selected: categoryFilters, onToggle: toggleCategoryFilter, onClear: () => setCategoryFilters(new Set()) },
              { key: "payment", label: "Payment Method", options: uniquePayments, selected: paymentFilters, onToggle: togglePaymentFilter, onClear: () => setPaymentFilters(new Set()) },
            ]}
            dateRange={{
              start: dateRangeStart,
              end: dateRangeEnd,
              onStartChange: (d) => { setDateRangeStart(d ?? undefined); setCurrentPage(1); },
              onEndChange: (d) => { setDateRangeEnd(d ?? undefined); setCurrentPage(1); },
              onClear: () => {
                setDateRangeStart(undefined);
                setDateRangeEnd(undefined);
                setCurrentPage(1);
              },
            }}
            extraControls={
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Button
                  size="sm"
                  className="h-7 w-7 p-0 text-[11px] sm:h-9 sm:w-auto sm:px-3 sm:text-sm"
                  onClick={() => {
                    if (!requireSubscription()) return;
                    openAddRecordSheet();
                  }}
                >
                  {isPreview ? <span className="mr-1"></span> : null}
                  <Plus className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Add Record</span>
                </Button>
              </div>
            }
          />
          </div>

          {entryMode !== "none" && (
            <div className="absolute inset-x-0 top-[58px] z-30 px-4 sm:px-10 pt-3">
              <div className={cn(entryMode === "bulk" ? "max-h-[72vh] overflow-y-auto" : "rounded-xl border border-border bg-background shadow-xl p-3 max-h-[72vh] overflow-y-auto")}>
                {entryMode === "bulk" && (
                  <div>
                    <BulkEntryTable onDone={handleEntryDone} />
                  </div>
                )}

                {entryMode === "import" && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-heading font-semibold text-foreground">Import from CSV</h2>
                      <Button variant="ghost" size="sm" onClick={handleEntryDone}>Close</Button>
                    </div>
                    <CsvImport onDone={handleEntryDone} />
                  </div>
                )}
              </div>
            </div>
          )}

          {columnsOpen && (
            <div>
              <TableColumnsPanel
                columns={finColumns.map(c => ({ key: c.key, label: c.label }))}
                visibleColumns={visibleFinCols as Set<string>}
                onToggle={(key) => toggleFinCol(key as FinColKey)}
                onClose={() => setColumnsOpen(false)}
                onReorder={(keys) => setFinColOrder(keys as FinColKey[])}
              />
            </div>
          )}
          <div className="min-h-[420px]">
          {isLoading ? (
            <div className="flex min-h-[420px] items-center justify-center text-muted-foreground">Loading...</div>
          ) : (
            <>
              {/* Mobile card view */}
              <div className="block sm:hidden divide-y divide-border/50">
                {pagedRecords.length === 0 && (
                  <div className="px-4 py-12 text-center text-sm text-muted-foreground">
                    {yearRecords.length === 0 ? "No finance records for this date range. Record your first income!" : "No records match your filters."}
                  </div>
                )}
                {pagedRecords.map((r) => {
                  const isEditing = inlineEditId === r.id && !!inlineEditValues;
                  const displayName = resolveMemberName(r);
                  const isExpense = (r as any).record_type === "expense";
                  const ministry = getOrganizationFromNotes(r.notes);
                  return (
                    <div
                      key={r.id}
                      className={cn(
                        "px-4 py-3 transition-[background-color,box-shadow]",
                        !isEditing && "cursor-pointer hover:bg-primary/10 hover:shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.22)]"
                      )}
                      onClick={() => {
                        if (!isEditing) setSelectedRecord(r);
                      }}
                    >
                      {isEditing ? (
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <Select value={inlineEditValues.record_type} onValueChange={(v) => setInlineEditValues((prev) => prev ? { ...prev, record_type: v as "income" | "expense" } : prev)}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="income">Income</SelectItem>
                                <SelectItem value="expense">Expense</SelectItem>
                              </SelectContent>
                            </Select>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" size="sm" className="h-8 justify-start text-left font-normal text-xs">
                                  <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
                                  {inlineEditValues.date ? format(parseISO(inlineEditValues.date), "MMM d, yyyy") : "Date"}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={inlineEditValues.date ? parseISO(inlineEditValues.date) : undefined}
                                  onSelect={(d) => {
                                    if (!d) return;
                                    setInlineEditValues((prev) => prev ? { ...prev, date: format(d, "yyyy-MM-dd") } : prev);
                                  }}
                                  className="pointer-events-auto"
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                          <Input value={inlineEditValues.category} onChange={(e) => setInlineEditValues((prev) => prev ? { ...prev, category: e.target.value } : prev)} className="h-8 text-xs" placeholder="Category" />
                          <Select
                            value={inlineEditValues.organization || "__none__"}
                            onValueChange={(v) => setInlineEditValues((prev) => prev ? { ...prev, organization: v === "__none__" ? "" : v } : prev)}
                          >
                            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Ministry" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">—</SelectItem>
                              {editableMinistryOptions.map((orgName) => (
                                <SelectItem key={orgName} value={orgName}>{orgName}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <MemberAutocomplete
                            value={inlineEditValues.member_name}
                            onChange={(val) => setInlineEditValues((prev) => prev ? { ...prev, member_name: val, member_id: null } : prev)}
                            onMemberSelect={(member) => setInlineEditValues((prev) => prev ? {
                              ...prev,
                              member_name: member?.full_name || prev.member_name,
                              member_id: member?.id || null,
                            } : prev)}
                            options={members as Array<{ id: string; full_name: string }>}
                            className="h-8 text-xs"
                            placeholder="Source"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <Input value={inlineEditValues.payment_method} onChange={(e) => setInlineEditValues((prev) => prev ? { ...prev, payment_method: e.target.value } : prev)} className="h-8 text-xs" placeholder="Payment" />
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground">$</span>
                              <Input
                                type="text"
                                inputMode="decimal"
                                value={formatAmountInput(inlineEditValues.amount)}
                                onChange={(e) => setInlineEditValues((prev) => prev ? { ...prev, amount: normalizeAmountInput(e.target.value) } : prev)}
                                className="h-8 text-xs text-left pl-5"
                                placeholder="Amount"
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-2 pt-1">
                            <Button size="sm" className="h-7 px-2 text-xs" onClick={() => saveInlineEdit(r)} disabled={updateMutation.isPending}>Save</Button>
                            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={cancelInlineEdit} disabled={updateMutation.isPending}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="grid w-full grid-cols-[72px_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1">
                          <span className={`inline-flex w-fit items-center px-2 py-0.5 rounded-md text-xs font-semibold ${isExpense ? "bg-destructive/10 text-destructive" : "bg-success/10 text-success"}`}>
                            {isExpense ? "Expense" : "Income"}
                          </span>
                          <p className="text-sm font-medium text-foreground truncate">{displayName || "—"}</p>
                          <p className={cn("text-sm font-semibold text-right whitespace-nowrap", isExpense ? "text-destructive" : "text-foreground")}>
                            {isExpense ? "-" : ""}{formatCurrency(Number(r.amount))}
                          </p>

                          <p className="text-[11px] text-muted-foreground whitespace-nowrap">{r.date ? format(parseISO(r.date), "MMM d") : "—"}</p>
                          <p className="text-xs text-muted-foreground truncate">{ministry || "—"}</p>
                          <span aria-hidden="true" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Desktop table view */}
              <div className="hidden sm:block w-full overflow-x-hidden">
                <table className="w-full min-w-full table-fixed">
                  <thead>
                    <tr className="border-b border-border bg-gray-200">
                      {finColumns.filter(c => visibleFinCols.has(c.key)).map((col) => (
                        <th
                          key={col.key}
                          className={`group text-sm font-semibold text-gray-700 tracking-wider px-4 py-2 cursor-pointer select-none hover:text-gray-900 transition-colors whitespace-nowrap text-left ${FIN_COL_WIDTH_CLASS[col.key]}`}
                          onClick={() => toggleFinSort(col.sortField)}
                        >
                          <span className="inline-flex items-center">
                            {col.label}
                            <FinSortIcon field={col.sortField} active={finSortCol === col.sortField && !!finSortDir} />
                          </span>
                        </th>
                      ))}
                      <th className="w-[8%] text-center text-sm font-semibold text-gray-700 tracking-wider px-2 py-2">
                        <TableSettingsGear
                          columns={finColumns.map(c => ({ key: c.key, label: c.label }))}
                          visibleColumns={visibleFinCols as Set<string>}
                          onToggle={(key) => toggleFinCol(key as FinColKey)}
                          open={columnsOpen}
                          onOpenChange={setColumnsOpen}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const newInlineRecord = inlineEditId === NEW_INLINE_EDIT_ID && inlineEditValues ? {
                        id: NEW_INLINE_EDIT_ID,
                        member_id: inlineEditValues.member_id,
                        member_name: inlineEditValues.member_name || null,
                        amount: Number(inlineEditValues.amount || 0),
                        date: inlineEditValues.date,
                        category: inlineEditValues.category,
                        record_type: inlineEditValues.record_type,
                        payment_method: inlineEditValues.payment_method || null,
                        notes: null,
                        created_by: user?.id || null,
                        created_at: "",
                        updated_at: "",
                      } as FinanceRecord : null;

                      const displayRecords = (() => {
                        if (!newInlineRecord || !inlineInsertAfterId) return pagedRecords;
                        if (inlineInsertAfterId === INLINE_INSERT_TOP) {
                          return [newInlineRecord, ...pagedRecords];
                        }
                        const insertAt = pagedRecords.findIndex((record) => record.id === inlineInsertAfterId);
                        if (insertAt === -1) return pagedRecords;
                        const list = [...pagedRecords];
                        list.splice(insertAt + 1, 0, newInlineRecord);
                        return list;
                      })();

                      if (displayRecords.length === 0) {
                        return (
                          yearRecords.length === 0 ? (
                            <tr>
                              <td colSpan={finColumns.filter(c => visibleFinCols.has(c.key)).length + 1} className="px-4 py-10 text-center text-sm text-muted-foreground bg-background">
                                No finance records for this date range. Record your first income!
                              </td>
                            </tr>
                          ) : (
                            <>
                              <tr className="h-[36px] border-b border-transparent">
                                <td colSpan={finColumns.filter(c => visibleFinCols.has(c.key)).length + 1} className="px-4 py-0.5" />
                              </tr>
                              <tr className="h-[36px] border-b border-transparent">
                                <td colSpan={finColumns.filter(c => visibleFinCols.has(c.key)).length + 1} className="px-4 py-0.5 text-center text-sm text-muted-foreground">
                                  No records match your filters.
                                </td>
                              </tr>
                              {Array.from({ length: Math.max(0, pageSize - 2) }).map((_, idx) => (
                                <tr key={`finance-empty-row-${idx}`} className={cn("h-[36px] border-b border-transparent", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                                  <td colSpan={finColumns.filter(c => visibleFinCols.has(c.key)).length + 1} className="px-4 py-0.5" />
                                </tr>
                              ))}
                            </>
                          )
                        );
                      }

                      const fillerCount = Math.max(0, pageSize - displayRecords.length);

                      return (
                        <>
                          {displayRecords.map((r, idx) => {
                            const isEditing = inlineEditId === r.id && !!inlineEditValues;
                            return (
                              <tr
                                key={r.id}
                                className={cn(
                                  "border-b border-transparent transition-[background-color,box-shadow]",
                                  isEditing ? "h-[46px]" : "h-[36px]",
                                  idx % 2 === 0 ? "bg-background" : "bg-muted/20",
                                  !isEditing && "cursor-pointer hover:bg-primary/10"
                                )}
                                onClick={() => {
                                  if (!isEditing) setSelectedRecord(r);
                                }}
                              >
                          {finColumns.filter(c => visibleFinCols.has(c.key)).map((col) => {
                            if (!isEditing) {
                              if (col.key === "date") return <td key="date" className="px-4 py-0.5 text-sm text-foreground whitespace-nowrap">{r.date ? format(parseISO(r.date), "MMM d, yyyy") : "—"}</td>;
                              if (col.key === "type") return <td key="type" className="px-4 py-0.5"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold ${(r as any).record_type === "expense" ? "bg-destructive/10 text-destructive" : "bg-success/10 text-success"}`}>{((r as any).record_type || "income").charAt(0).toUpperCase() + ((r as any).record_type || "income").slice(1)}</span></td>;
                              if (col.key === "category") return <td key="category" className="px-4 py-0.5 whitespace-nowrap"><Badge variant="secondary" className="rounded-md">{r.category}</Badge></td>;
                              if (col.key === "organization") return <td key="organization" className="px-4 py-0.5 text-sm text-muted-foreground">{getOrganizationFromNotes(r.notes) || "—"}</td>;
                              if (col.key === "member") {
                                const displayName = resolveMemberName(r);
                                return <td key="member" className="px-4 py-0.5 max-w-[180px] truncate">{displayName ? <MemberNameCell name={displayName} iconStyle="avatar" /> : <span className="text-sm text-muted-foreground">—</span>}</td>;
                              }
                              if (col.key === "payment") return <td key="payment" className="px-4 py-0.5 text-sm text-muted-foreground whitespace-nowrap">{r.payment_method || "—"}</td>;
                              if (col.key === "amount") return <td key="amount" className={`px-4 py-0.5 text-sm font-semibold text-left whitespace-nowrap ${(r as any).record_type === "expense" ? "text-destructive" : "text-foreground"}`}>{(r as any).record_type === "expense" ? `-${formatCurrency(Number(r.amount))}` : formatCurrency(Number(r.amount))}</td>;
                              return null;
                            }

                            if (col.key === "date") {
                              return (
                                <td key="date" className="px-4 py-1.5">
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <Button variant="outline" size="sm" className="h-8 w-full justify-start text-left font-normal">
                                        <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
                                        {inlineEditValues.date ? format(parseISO(inlineEditValues.date), "MM/dd/yyyy") : "Date"}
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                      <Calendar
                                        mode="single"
                                        selected={inlineEditValues.date ? parseISO(inlineEditValues.date) : undefined}
                                        onSelect={(d) => {
                                          if (!d) return;
                                          setInlineEditValues((prev) => prev ? { ...prev, date: format(d, "yyyy-MM-dd") } : prev);
                                        }}
                                        className="pointer-events-auto"
                                      />
                                    </PopoverContent>
                                  </Popover>
                                </td>
                              );
                            }
                            if (col.key === "type") {
                              return (
                                <td key="type" className="px-4 py-1.5">
                                  <Select value={inlineEditValues.record_type} onValueChange={(v) => setInlineEditValues((prev) => {
                                    if (!prev) return prev;
                                    const nextType = v as "income" | "expense";
                                    const nextOptions = nextType === "expense" ? expenseCategoryOptions : incomeCategoryOptions;
                                    const nextCategory = nextOptions.includes(prev.category) ? prev.category : (nextOptions[0] || "");
                                    return { ...prev, record_type: nextType, category: nextCategory };
                                  })}>
                                    <SelectTrigger className="h-8 w-full"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="income">Income</SelectItem>
                                      <SelectItem value="expense">Expense</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </td>
                              );
                            }
                            if (col.key === "category") {
                              const categoryOptions = inlineEditValues.record_type === "expense" ? expenseCategoryOptions : incomeCategoryOptions;
                              return (
                                <td key="category" className="px-4 py-1.5">
                                  <Select value={inlineEditValues.category || "__none__"} onValueChange={(v) => { setInlineErrors((prev) => { const next = new Set(prev); next.delete("category"); return next; }); setInlineEditValues((prev) => prev ? { ...prev, category: v === "__none__" ? "" : v } : prev); }}>
                                    <SelectTrigger className={cn("h-8 w-full", inlineErrors.has("category") && "border-red-500 ring-1 ring-red-500")}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__none__">Select category</SelectItem>
                                      {categoryOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </td>
                              );
                            }
                            if (col.key === "organization") {
                              return (
                                <td key="organization" className="px-4 py-1.5">
                                  <Select
                                    value={inlineEditValues.organization || "__none__"}
                                    onValueChange={(v) => setInlineEditValues((prev) => prev ? { ...prev, organization: v === "__none__" ? "" : v } : prev)}
                                  >
                                    <SelectTrigger className="h-8 w-full"><SelectValue placeholder="Ministry" /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__none__">—</SelectItem>
                                      {editableMinistryOptions.map((orgName) => <SelectItem key={orgName} value={orgName}>{orgName}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </td>
                              );
                            }
                            if (col.key === "member") {
                              return (
                                <td key="member" className="px-4 py-1.5">
                                  <MemberAutocomplete
                                    value={inlineEditValues.member_name}
                                    onChange={(val) => setInlineEditValues((prev) => prev ? { ...prev, member_name: val, member_id: null } : prev)}
                                    onMemberSelect={(member) => setInlineEditValues((prev) => prev ? {
                                      ...prev,
                                      member_name: member?.full_name || prev.member_name,
                                      member_id: member?.id || null,
                                    } : prev)}
                                    options={members as Array<{ id: string; full_name: string }>}
                                    className="h-8 w-full"
                                    placeholder="Source"
                                  />
                                </td>
                              );
                            }
                            if (col.key === "payment") {
                              return (
                                <td key="payment" className="px-4 py-1.5">
                                  <Select
                                    value={inlineEditValues.payment_method || "__none__"}
                                    onValueChange={(v) => { setInlineErrors((prev) => { const next = new Set(prev); next.delete("payment_method"); return next; }); setInlineEditValues((prev) => prev ? { ...prev, payment_method: v === "__none__" ? "" : v } : prev); }}
                                  >
                                    <SelectTrigger className={cn("h-8 w-full", inlineErrors.has("payment_method") && "border-red-500 ring-1 ring-red-500")}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__none__">—</SelectItem>
                                      {uniquePayments.filter((p) => p !== "—").map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </td>
                              );
                            }
                            if (col.key === "amount") {
                              return (
                                <td key="amount" className="px-4 py-1.5">
                                  <div className="relative w-full">
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                                    <Input
                                      type="text"
                                      inputMode="decimal"
                                      value={formatAmountInput(inlineEditValues.amount)}
                                      onChange={(e) => { setInlineErrors((prev) => { const next = new Set(prev); next.delete("amount"); return next; }); setInlineEditValues((prev) => prev ? { ...prev, amount: normalizeAmountInput(e.target.value) } : prev); }}
                                      className={cn("h-8 text-left pl-5", inlineErrors.has("amount") && "border-red-500 ring-1 ring-red-500")}
                                    />
                                  </div>
                                </td>
                              );
                            }
                            return null;
                          })}
                          <td className="px-2 py-1.5 text-center whitespace-nowrap w-[8%]">
                            {isEditing ? (
                              <div className="inline-flex items-center justify-center gap-2">
                                <Button size="icon" className="h-7 w-7" onClick={() => saveInlineEdit(r)} disabled={updateMutation.isPending}>
                                  <Check className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="outline" size="icon" className="h-7 w-7" onClick={cancelInlineEdit} disabled={updateMutation.isPending}>
                                  <X className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            ) : (
                              <div className="inline-flex items-center justify-center">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button className="inline-flex items-center justify-center p-1 rounded-lg hover:bg-secondary transition-colors">
                                      <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="rounded-lg">
                                    <DropdownMenuItem onClick={(e) => {
                                      e.stopPropagation();
                                      if (!requireSubscription()) return;
                                      openEditRecordSheet(r);
                                    }}>
                                      <Pencil className="w-4 h-4 mr-2" /> Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={(e) => {
                                      e.stopPropagation();
                                      if (!requireSubscription()) return;
                                      setDeleteId(r.id);
                                    }} className="text-destructive focus:text-destructive">
                                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            )}
                          </td>
                              </tr>
                            );
                          })}
                          {Array.from({ length: fillerCount }).map((_, idx) => (
                            <tr key={`finance-empty-row-fill-${idx}`} className={cn("h-[36px] border-b border-transparent", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                              <td colSpan={finColumns.filter(c => visibleFinCols.has(c.key)).length + 1} className="px-4 py-0.5" />
                            </tr>
                          ))}
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 px-4 py-3 border-t border-border/40">
                  <p className="text-xs text-muted-foreground">
                    {filteredYearRecords.length === 0
                      ? "Showing 0-0 of 0"
                      : `Showing ${financeDisplayRangeStart}-${financeDisplayRangeEnd} of ${filteredYearRecords.length}`}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground">Rows per page:</span>
                    <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
                      <SelectTrigger className="h-7 w-20 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                    {totalPages > 1 && (
                      <>
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>«</Button>
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>‹</Button>
                        <span className="text-xs px-2">{currentPage} / {totalPages}</span>
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>›</Button>
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>»</Button>
                      </>
                    )}
                    </div>
                  </div>
          </>
          )}
          </div>
          </div>
        </TabsContent>

        <TabsContent value="annual_budget">
          <AnnualBudget viewYear={viewYear} setViewYear={setViewYear} years={years} formatCurrency={formatCurrency} incomeRecords={filteredByFinanceScope} />
        </TabsContent>

        {canViewProjectBudgets && (
          <TabsContent value="project_budgets">
            <ProjectBudgets formatCurrency={formatCurrency} />
          </TabsContent>
        )}

        {canViewStatements && (
          <TabsContent value="statements">
            <ReportsHub formatCurrency={formatCurrency} />
          </TabsContent>
        )}

        <TabsContent value="banking">
          <BankingTab />
        </TabsContent>
      </Tabs>

      <Dialog open={!!selectedRecord} onOpenChange={(open) => !open && setSelectedRecord(null)}>
        <DialogContent className="max-w-3xl w-full mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Record Details</DialogTitle>
          </DialogHeader>

          {selectedRecord && (
            <div className="space-y-4">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Type</p>
                    <p className="font-medium text-foreground">{selectedRecord.record_type === "expense" ? "Expense" : "Income"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Date</p>
                    <p className="font-medium text-foreground">{selectedRecord.date ? format(parseISO(selectedRecord.date), "MMMM d, yyyy") : "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Category</p>
                    <p className="font-medium text-foreground">{selectedRecord.category || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Ministry</p>
                    <p className="font-medium text-foreground">{getOrganizationFromNotes(selectedRecord.notes) || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Source</p>
                    <p className="font-medium text-foreground">{resolveMemberName(selectedRecord) || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Payment</p>
                    <p className="font-medium text-foreground">{selectedRecord.payment_method || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Amount</p>
                    <p className={cn("font-semibold", selectedRecord.record_type === "expense" ? "text-destructive" : "text-foreground")}>
                      {selectedRecord.record_type === "expense" ? "-" : ""}{formatCurrency(Number(selectedRecord.amount || 0))}
                    </p>
                  </div>
                </div>
                {selectedRecord.notes && (
                  <div className="mt-4">
                    <p className="text-xs text-muted-foreground">Notes</p>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{selectedRecord.notes}</p>
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                <h3 className="text-2xl font-heading font-semibold text-foreground">Record Info</h3>
                <div>
                  <p className="text-xs text-muted-foreground">Entered By</p>
                  <p className="text-base font-medium text-foreground">{resolveMemberName(selectedRecord) || "—"}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Created</p>
                    <p className="text-base font-medium text-foreground">{formatRecordTimestamp(selectedRecord.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Created By</p>
                    <p className="text-base font-medium text-foreground">{selectedRecord.created_by ? (profileNameByUserId.get(selectedRecord.created_by) || "Unknown User") : "Automations"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Last Modified Time</p>
                    <p className="text-base font-medium text-foreground">{formatRecordTimestamp(selectedRecord.updated_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Last Modified By</p>
                    <p className="text-base font-medium text-foreground">{selectedRecord.created_by ? (profileNameByUserId.get(selectedRecord.created_by) || "Unknown User") : "Automations"}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" onClick={() => setSelectedRecord(null)}>
                  Close
                </Button>
                <Button
                  onClick={() => {
                    if (!requireSubscription()) return;
                    openEditRecordSheet(selectedRecord);
                    setSelectedRecord(null);
                  }}
                >
                  <Pencil className="w-4 h-4 mr-2" /> Edit Record
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

{/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently remove the finance record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!requireSubscription()) return;
                if (deleteId) deleteMutation.mutate(deleteId);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <FinanceFormDialog
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        record={sheetRecord}
      />
    </div>
  );
};

export default Finance;
